function resolveApiBase() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') return `${window.location.origin}/api/v1`;
  return 'http://localhost:3001/api/v1';
}

function resolveAiBase() {
  // 经主服务 /api/v1/ai 转发给第三方大模型（生产：DeepSeek api.deepseek.com，由服务端环境变量配置）
  // 注：这里只描述链路，具体服务商以服务端配置为准，不要在此写死域名。
  if (process.env.REACT_APP_AI_URL) return process.env.REACT_APP_AI_URL;
  return resolveApiBase();
}

function resolveWsBase() {
  if (process.env.REACT_APP_WS_URL) return process.env.REACT_APP_WS_URL;
  if (typeof window !== 'undefined') {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/ws`;
  }
  return 'ws://localhost:3001/ws';
}

const API_BASE = resolveApiBase();
const AI_BASE = resolveAiBase();
const WS_BASE = resolveWsBase();

// Token管理
const TOKEN_KEY = 'pengji_token';
const USER_KEY = 'pengji_user';

// 默认请求超时（ms）。慢网络下避免请求永久挂起，拖死交互反馈。
const DEFAULT_TIMEOUT_MS = 15000;
// 401 去重：并发多个 401 只触发一次跳转，避免 SPA 全量重载风暴
let redirecting401 = false;

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function getUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
}

export function setUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function removeUser() {
  localStorage.removeItem(USER_KEY);
}

/**
 * 安全解析 JSON 响应。
 * 204 No Content 或空 body 不会抛错，返回 null —— 旧实现会直接 crash。
 */
async function parseBody(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    // 非 JSON 响应（如纯文本错误页）——回退为带原文的对象，避免上层拿到 undefined
    return { error: text.slice(0, 200) };
  }
}

/**
 * 构建可超时的 fetch signal。
 * - 若调用方传入 signal，则尊重调用方（不覆盖）。
 * - 否则用 AbortSignal.timeout，到点自动中止，避免请求挂死。
 */
function buildSignal(options) {
  if (options.signal) return options.signal;
  const ms = options.timeoutMs || DEFAULT_TIMEOUT_MS;
  if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
    return AbortSignal.timeout(ms);
  }
  // 老环境降级：手动 AbortController
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

// 通用请求方法
async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const config = {
    ...options,
    headers,
    signal: buildSignal(options),
  };

  if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
    config.body = JSON.stringify(config.body);
  }

  const baseUrl = options._aiService ? AI_BASE : API_BASE;
  delete config._aiService;
  delete config.timeoutMs;

  let response;
  try {
    response = await fetch(`${baseUrl}${path}`, config);
  } catch (err) {
    // 超时 / 网络中断 —— 抛出可读错误，而非原始 TypeError
    if (err?.name === 'TimeoutError' || err?.name === 'AbortError') {
      throw new Error('请求超时，请检查网络后重试');
    }
    throw new Error('网络连接失败，请稍后重试');
  }

  const data = await parseBody(response);

  if (!response.ok) {
    // ── 哪些 401 不该被当成「登录态失效」────────────────────────────
    //   ① 未登录时就该能打的端点（登录/注册/能力探测/查重/找回）：
    //      401 只代表"这次凭据不对"。清 session 会让用户在登录页看到一次莫名登出。
    //   ② change-password：它的 401 是 INVALID_CURRENT_PASSWORD（当前密码填错），
    //      不是 token 失效。清 session 会把正在强制改密的用户直接锁死 ——
    //      改密需要 token，token 被清掉又要重新登录，而他的密码就是那一串临时密码。
    const AUTH_401_SESSION_SAFE = [
      '/identity/login',
      '/identity/register',
      '/identity/capabilities',
      '/identity/username-availability',
      '/identity/recovery-request',
      '/identity/change-password',
    ];
    const sessionSafe401 = AUTH_401_SESSION_SAFE.some((p) => path.includes(p));

    if (response.status === 401 && !sessionSafe401) {
      removeToken();
      removeUser();
      // SPA 跳转而非整页 reload：保留路由状态、避免白屏闪烁
      if (!redirecting401) {
        redirecting401 = true;
        // 用 setTimeout(0) 让当前调用栈收尾，避免在 reject 链里触发路由抖动
        setTimeout(() => {
          redirecting401 = false;
          if (window.location.pathname !== '/login') {
            window.location.replace('/login');
          }
        }, 0);
      }
    }
    const err = new Error(data?.error || `请求失败（${response.status}）`);
    // 带上状态码，调用方可区分「真未登录(401)」与「限流/抖动(429/5xx)」，避免误杀会话
    err.status = response.status;
    // 带上业务错误码。契约里明确要求：**客户端按 code 分支，不要按 message 文案分支**
    // —— 文案会为了体验反复改，按文案分支的功能会随文案一起烂掉。
    err.code = data?.code;
    throw err;
  }

  return data;
}

/** 公开接口：失败不踢登录态 */
async function publicRequest(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  const config = { ...options, headers, signal: buildSignal(options) };
  if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
    config.body = JSON.stringify(config.body);
  }
  delete config.timeoutMs;

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, config);
  } catch (err) {
    if (err?.name === 'TimeoutError' || err?.name === 'AbortError') {
      throw new Error('请求超时，请检查网络后重试');
    }
    throw new Error('网络连接失败，请稍后重试');
  }
  const data = await parseBody(response);
  if (!response.ok) throw new Error(data?.error || `请求失败（${response.status}）`);
  return data;
}

// API方法
export const api = {
  // 认证
  auth: {
    config: () => request('/auth/config'),

    // ── 身份（用户名 + 密码）──────────────────────────────────────────
    // 手机号 + 短信验证码登录已于 2026-10 整体退役，服务端对应端点现在返回 410 Gone。
    // 新的登录面在 modules/identity-password，挂在 /api/v1/identity/*。
    // 这里刻意不再提供 sendCode / devCode / loginWithCode —— 少一个方法，
    // 就少一处会被误用的旧入口。

    /** 登录页的唯一真相源：登录方式、用户名/密码规则、有没有自助找回 */
    identityCapabilities: () => request('/identity/capabilities'),
    /** 注册前查重。格式不合法时服务端不查库，直接返回原因 */
    usernameAvailability: (username) =>
      request(`/identity/username-availability?username=${encodeURIComponent(username)}`),
    /** 登录。响应里的 user.mustChangePassword 决定要不要先强制改密 */
    identityLogin: (username, password) =>
      request('/identity/login', { method: 'POST', body: { username, password } }),
    identityRegister: (username, password, nickname) =>
      request('/identity/register', { method: 'POST', body: { username, password, nickname } }),
    /** 改密。服务端会换发 token，必须用返回值里的新 token 覆盖本地 */
    identityChangePassword: (currentPassword, newPassword) =>
      request('/identity/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      }),
    /** 人工找回工单。服务端对"用户名存在/不存在"返回相同结果，客户端不做存在性判断 */
    identityRecoveryRequest: (username, contact, note) =>
      request('/identity/recovery-request', {
        method: 'POST',
        body: { username, contact, note },
      }),

    me: () => request('/auth/me'),
    updateMe: (data) => request('/auth/me', { method: 'PUT', body: data }),
    // 搜索口径只有一条：按用户名（服务端 /auth/search 的查询字段就是 users.username）。
    // 以前这里传的是 ?phone=，服务端虽然还兼容这个参数名，但查的同样是 username ——
    // 于是"用手机号当用户名传进去"只对迁移老账号碰巧有效，新账号永远搜不到。
    search: (username) => request(`/auth/search?username=${encodeURIComponent(username)}`),
    uploadKeys: (encryptionPublicKey, signingPublicKey) =>
      request('/auth/keys/upload', { method: 'POST', body: { encryptionPublicKey, signingPublicKey } }),
    getUserKeys: (userId) => request(`/auth/keys/${userId}`),
    // 数据导出
    exportData: () => request('/auth/export', { method: 'POST' }),
    getExports: () => request('/auth/exports'),
    // 账号删除（立即擦除，兼容）
    deleteAccount: (confirmPhrase) =>
      request('/auth/account', { method: 'DELETE', body: { confirm_phrase: confirmPhrase } }),
    requestDeletion: (confirmPhrase, reason = '') =>
      request('/auth/account/deletion-request', {
        method: 'POST',
        body: { confirm_phrase: confirmPhrase, reason },
      }),
    deletionStatus: () => request('/auth/account/deletion-status'),
    cancelDeletion: () => request('/auth/account/deletion-cancel', { method: 'POST' }),
    // 设备管理
    getDevices: () => request('/auth/devices'),
    addDevice: (data) => request('/auth/devices', { method: 'POST', body: data }),
    removeDevice: (deviceId) => request(`/auth/devices/${deviceId}`, { method: 'DELETE' }),
  },

  // 好友
  // 创始人后台控制台（modules/admin-console）：全部只读，服务端有 登录+创始人 两道闸。
  // 403 = 当前登录人不在 FOUNDER_PHONES/FOUNDER_USERNAMES 名单里，如实展示即可。
  adminConsole: {
    overview: () => request('/admin-console/overview'),
    users: (q, page, pageSize) =>
      request(`/admin-console/users?q=${encodeURIComponent(q || '')}&page=${page || 1}${pageSize ? `&pageSize=${pageSize}` : ''}`),
    circles: (page, pageSize) =>
      request(`/admin-console/circles?page=${page || 1}${pageSize ? `&pageSize=${pageSize}` : ''}`),
    ops: () => request('/admin-console/ops'),
    triggerBackup: () => request('/admin-console/ops/backup/trigger', { method: 'POST' }),
    verifyBackup: (filename) =>
      request(`/admin-console/ops/backup/verify/${encodeURIComponent(filename)}`, { method: 'POST' }),
    audit: (page, pageSize, action) =>
      request(`/admin-console/audit?page=${page || 1}${pageSize ? `&pageSize=${pageSize}` : ''}${action ? `&action=${encodeURIComponent(action)}` : ''}`),

    // ---- 写能力（服务端两道闸：登录 + 创始人名单；这里不做权限判断）----
    ban: (userId, reason) =>
      request(`/admin-console/users/${encodeURIComponent(userId)}/ban`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || '' }),
      }),
    unban: (userId) =>
      request(`/admin-console/users/${encodeURIComponent(userId)}/unban`, { method: 'POST' }),
    kick: (userId) =>
      request(`/admin-console/users/${encodeURIComponent(userId)}/kick`, { method: 'POST' }),
    updateProfile: (userId, patch) =>
      request(`/admin-console/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch || {}),
      }),
    // 返回值里的 tempPassword 只出现一次，服务端不留存
    resetPassword: (userId) =>
      request(`/admin-console/users/${encodeURIComponent(userId)}/reset-password`, { method: 'POST' }),

    // 报表导出：走真实下载（blob + 触发保存），而不是把 CSV 文本读进内存再拼界面。
    // 刻意【不用】把 token 放进 URL 再 <a href> 跳转：URL 会进访问日志、浏览器历史、
    // Referer 头，等于把长期有效的登录令牌抄送了好几处。这里照常走 Authorization 头。
    downloadReport: async (kind) => {
      const res = await fetch(`${API_BASE}/admin-console/reports/${kind}.csv`, {
        headers: { Authorization: `Bearer ${getToken() || ''}` },
      });
      if (!res.ok) {
        let message = `导出失败（${res.status}）`;
        try {
          const j = await res.json();
          if (j && j.error) message = j.error;
        } catch { /* 非 JSON 响应就用状态码兜底 */ }
        throw new Error(message);
      }
      const blob = await res.blob();
      // 文件名优先取服务端给的 UTF-8 名（filename*=UTF-8''…），中文才不会变乱码
      const disposition = res.headers.get('content-disposition') || '';
      const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      const plain = /filename="([^"]+)"/i.exec(disposition);
      let filename = `${kind}.csv`;
      if (star) {
        try { filename = decodeURIComponent(star[1]); } catch { filename = plain ? plain[1] : filename; }
      } else if (plain) {
        filename = plain[1];
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // 立刻回收：不 revoke 会让整份报表一直留在内存里（大表上很可观）
      setTimeout(() => URL.revokeObjectURL(url), 0);
      return { filename, size: blob.size };
    },
  },

  friends: {
    list: () => request('/friends/list'),
    pending: () => request('/friends/pending'),
    invite: (friend_id, circle_id) =>
      request('/friends/invite', { method: 'POST', body: { friend_id, circle_id } }),
    accept: (id) => request(`/friends/accept/${id}`, { method: 'POST' }),
    reject: (id) => request(`/friends/reject/${id}`, { method: 'POST' }),
    remove: (friendId) => request(`/friends/${friendId}`, { method: 'DELETE' }),
  },

  // 圈层
  circles: {
    my: () => request('/circles/my'),
    create: (data) => request('/circles/create', { method: 'POST', body: data }),
    detail: (id) => request(`/circles/${id}`),
    updateSettings: (circleId, data) =>
      request(`/circles/${circleId}/settings`, { method: 'PUT', body: data }),
    addMember: (circleId, user_id, role) =>
      request(`/circles/${circleId}/members`, { method: 'POST', body: { user_id, role } }),
    removeMember: (circleId, userId) =>
      request(`/circles/${circleId}/members/${userId}`, { method: 'DELETE' }),
    moments: (circleId) => request(`/circles/${circleId}/moments`),
    postMoment: (circleId, data) =>
      request(`/circles/${circleId}/moments`, { method: 'POST', body: data }),
  },

  compliance: {
    versions: () => publicRequest('/compliance/versions'),
    doc: (docType) => publicRequest(`/compliance/docs/${docType}`),
    consentStatus: () => request('/compliance/consent/status'),
    agree: (data) => request('/compliance/consent', { method: 'POST', body: data }),
  },

  safety: {
    types: () => request('/safety/types'),
    createReport: (data) => request('/safety/reports', { method: 'POST', body: data }),
    mine: () => request('/safety/reports/mine'),
    founderList: (status = 'open') =>
      request(`/safety/founder/reports?status=${encodeURIComponent(status)}`),
    founderUpdate: (id, data) =>
      request(`/safety/founder/reports/${id}`, { method: 'PUT', body: data }),
  },

  // 亲密度阶梯
  intimacy: {
    config: () => publicRequest('/intimacy/config'),
    me: (circleId) => request(`/intimacy/circles/${circleId}/me`),
    evidence: (circleId, userId) => request(`/intimacy/circles/${circleId}/evidence/${userId}`),
    apply: (circleId, toTier) =>
      request(`/intimacy/circles/${circleId}/apply`, { method: 'POST', body: { toTier } }),
    applications: (circleId, status = 'pending') =>
      request(`/intimacy/circles/${circleId}/applications?status=${encodeURIComponent(status)}`),
    decide: (circleId, appId, decision, note = '') =>
      request(`/intimacy/circles/${circleId}/applications/${appId}/decide`, {
        method: 'POST',
        body: { decision, note },
      }),
    setTier: (circleId, userId, intimacyTier) =>
      request(`/intimacy/circles/${circleId}/members/${userId}/tier`, {
        method: 'PUT',
        body: { intimacyTier },
      }),
  },

  // 消息
  messages: {
    sendPrivate: (data) => request('/messages/private', { method: 'POST', body: data }),
    sendCircle: (data) => request('/messages/circle', { method: 'POST', body: data }),
    privateHistory: (userId, params = {}) => {
      const query = new URLSearchParams(params).toString();
      return request(`/messages/private/${userId}?${query}`);
    },
    circleHistory: (circleId, params = {}) => {
      const query = new URLSearchParams(params).toString();
      return request(`/messages/circle/${circleId}?${query}`);
    },
    clear: (targetId, type) =>
      request(`/messages/clear/${targetId}?type=${type}`, { method: 'DELETE' }),
    unread: () => request('/messages/unread'),
    revoke: (messageId) => request(`/messages/${messageId}/revoke`, { method: 'POST' }),
    // 文件上传
    upload: (formData) => request('/messages/upload', {
      method: 'POST',
      body: formData,
      headers: {}, // 让浏览器自动设置 multipart/form-data
    }),
    downloadFile: (fileId) => request(`/messages/file/${fileId}`),
  },

  // 共享清单
  lists: {
    circleLists: (circleId) => request(`/lists/circle/${circleId}`),
    create: (data) => request('/lists/create', { method: 'POST', body: data }),
    detail: (id) => request(`/lists/${id}`),
    addItem: (listId, data) => request(`/lists/${listId}/items`, { method: 'POST', body: data }),
    updateItem: (listId, itemId, data) =>
      request(`/lists/${listId}/items/${itemId}`, { method: 'PUT', body: data }),
    deleteItem: (listId, itemId) =>
      request(`/lists/${listId}/items/${itemId}`, { method: 'DELETE' }),
    deleteList: (id) => request(`/lists/${id}`, { method: 'DELETE' }),
  },

  // 投票
  polls: {
    circlePolls: (circleId) => request(`/polls/circle/${circleId}`),
    create: (data) => request('/polls/create', { method: 'POST', body: data }),
    detail: (id) => request(`/polls/${id}`),
    vote: (id, option_indices) =>
      request(`/polls/${id}/vote`, { method: 'POST', body: { option_indices } }),
    close: (id) => request(`/polls/${id}/close`, { method: 'PUT' }),
  },

  // Widget
  widgets: {
    push: (data) => request('/widgets/push', { method: 'POST', body: data }),
    received: (limit) => request(`/widgets/received?limit=${limit || 20}`),
    sent: (limit) => request(`/widgets/sent?limit=${limit || 20}`),
    snapshot: () => request('/widgets/snapshot'),
  },

  // 纪念日
  anniversaries: {
    circleList: (circleId) => request(`/anniversaries/circle/${circleId}`),
    create: (data) => request('/anniversaries/create', { method: 'POST', body: data }),
    delete: (id) => request(`/anniversaries/${id}`, { method: 'DELETE' }),
    upcoming: (days) => request(`/anniversaries/upcoming${days ? `?days=${days}` : ''}`),
  },

  // 树洞（单人首体验，不依赖圈层）
  treehole: {
    addNote: (content, mood) => request('/treehole/notes', { method: 'POST', body: { content, mood } }),
    listNotes: () => request('/treehole/notes'),
  },

  kinship: {
    config: () => publicRequest('/kinship/config'),
    presence: (circleId) => request(`/kinship/circles/${circleId}/presence`),
    checkIn: (circleId, data) =>
      request(`/kinship/circles/${circleId}/presence`, { method: 'POST', body: data }),
    fairness: (circleId) => request(`/kinship/circles/${circleId}/fairness`),
    shareFairness: (circleId) =>
      request(`/kinship/circles/${circleId}/fairness/share`, { method: 'POST' }),
    getFairnessShare: (token) => publicRequest(`/kinship/fairness/share/${token}`),
    events: (circleId, type) =>
      request(`/kinship/circles/${circleId}/events${type ? `?type=${type}` : ''}`),
    createEvent: (circleId, data) =>
      request(`/kinship/circles/${circleId}/events`, { method: 'POST', body: data }),
    deleteEvent: (circleId, eventId) =>
      request(`/kinship/circles/${circleId}/events/${eventId}`, { method: 'DELETE' }),
    repairs: (circleId) => request(`/kinship/circles/${circleId}/repairs`),
    startRepair: (circleId, data) =>
      request(`/kinship/circles/${circleId}/repairs`, { method: 'POST', body: data }),
    resolveRepair: (circleId, repairId) =>
      request(`/kinship/circles/${circleId}/repairs/${repairId}/resolve`, { method: 'POST' }),
    emergency: (circleId) => request(`/kinship/circles/${circleId}/emergency`),
    setKinshipRole: (circleId, userId, kinshipRole) =>
      request(`/kinship/circles/${circleId}/members/${userId}/role`, {
        method: 'PUT',
        body: { kinshipRole },
      }),
    careBoard: (circleId) => request(`/kinship/circles/${circleId}/care-board`),
    ease: (circleId) => request(`/kinship/circles/${circleId}/ease`),
    shareEase: (circleId) =>
      request(`/kinship/circles/${circleId}/ease/share`, { method: 'POST' }),
    getEaseShare: (token) => publicRequest(`/kinship/ease/share/${token}`),
    favors: (circleId, memberId) =>
      request(
        `/kinship/circles/${circleId}/favors${memberId ? `?memberId=${encodeURIComponent(memberId)}` : ''}`
      ),
    favorSummary: (circleId) =>
      request(`/kinship/circles/${circleId}/favors/summary`),
    createFavor: (circleId, data) =>
      request(`/kinship/circles/${circleId}/favors`, { method: 'POST', body: data }),
    shareFavors: (circleId) =>
      request(`/kinship/circles/${circleId}/favors/share`, { method: 'POST' }),
    getFavorShare: (token) => publicRequest(`/kinship/favors/share/${token}`),
    draftFavorsFromEvent: (circleId, data) =>
      request(`/kinship/circles/${circleId}/favors/draft-from-event`, {
        method: 'POST',
        body: data,
      }),
    confirmFavor: (circleId, entryId, data) =>
      request(`/kinship/circles/${circleId}/favors/${entryId}/confirm`, {
        method: 'POST',
        body: data || {},
      }),
    deleteFavor: (circleId, entryId) =>
      request(`/kinship/circles/${circleId}/favors/${entryId}`, { method: 'DELETE' }),
  },

  lifePacks: {
    config: () => publicRequest('/kinship/life-packs/config'),
    list: (circleId) => request(`/kinship/circles/${circleId}/life-packs`),
    activate: (circleId, packId) =>
      request(`/kinship/circles/${circleId}/life-packs/${packId}/activate`, {
        method: 'POST',
        body: {},
      }),
    archive: (circleId, packId) =>
      request(`/kinship/circles/${circleId}/life-packs/${packId}/archive`, {
        method: 'POST',
        body: {},
      }),
  },

  familyPlan: {
    config: () => publicRequest('/family-plan/config'),
    intent: (data) => request('/family-plan/intent', { method: 'POST', body: data }),
    subscription: (circleId) => request(`/family-plan/circles/${circleId}/subscription`),
    createOrder: (circleId, data) =>
      request(`/family-plan/circles/${circleId}/orders`, { method: 'POST', body: data || {} }),
    sandboxPay: (orderNo) =>
      request(`/family-plan/sandbox/pay/${orderNo}`, { method: 'POST', body: {} }),
  },

  elderOnboard: {
    preview: (token) => publicRequest(`/elder-onboard/${token}`),
    open: (token) => publicRequest(`/elder-onboard/${token}/open`, { method: 'POST', body: {} }),
    create: (circleId, data) =>
      request(`/elder-onboard/circles/${circleId}`, { method: 'POST', body: data }),
    list: (circleId) => request(`/elder-onboard/circles/${circleId}`),
    helpCard: (token) =>
      request(`/elder-onboard/${token}/help-card`, { method: 'POST', body: {} }),
    claim: (token) => request(`/elder-onboard/${token}/claim`, { method: 'POST', body: {} }),
    presence: (token, data) =>
      request(`/elder-onboard/${token}/presence`, { method: 'POST', body: data }),
    voice: (token) => request(`/elder-onboard/${token}/voice`, { method: 'POST', body: {} }),
    home: (token) => request(`/elder-onboard/${token}/home`),
  },

  memorials: {
    generate: (data) => request('/memorials/generate', { method: 'POST', body: data }),
    list: () => request('/memorials'),
    detail: (id) => request(`/memorials/${id}`),
    publish: (id) => request(`/memorials/${id}/publish`, { method: 'POST' }),
    share: (token) => publicRequest(`/memorials/share/${token}`),
  },

  // 通话
  calls: {
    history: () => request('/calls/history'),
    deleteRecord: (callId) => request(`/calls/history/${callId}`, { method: 'DELETE' }),
    clearHistory: () => request('/calls/history', { method: 'DELETE' }),
    iceConfig: () => request('/calls/ice-config'),
  },

  // === v3.0 去中心化 ===

  // IPFS 分布式存储
  ipfs: {
    status: () => request('/ipfs/status'),
    upload: (formData) => request('/ipfs/upload', {
      method: 'POST',
      body: formData,
      headers: {},
    }),
    uploadText: (content, metadata) =>
      request('/ipfs/upload-text', { method: 'POST', body: { content, metadata } }),
    getFile: (cid) => request(`/ipfs/file/${cid}`),
    check: (cid) => request(`/ipfs/check/${cid}`),
    pin: (cid) => request(`/ipfs/pin/${cid}`, { method: 'POST' }),
  },

  // Passkey 本地身份认证
  passkey: {
    registerOptions: (nickname) =>
      request('/passkey/register/options', { method: 'POST', body: { nickname } }),
    registerVerify: (data) =>
      request('/passkey/register/verify', { method: 'POST', body: data }),
    loginOptions: () =>
      request('/passkey/login/options', { method: 'POST', body: {} }),
    loginVerify: (data) =>
      request('/passkey/login/verify', { method: 'POST', body: data }),
    checkDevice: () => request('/passkey/check-device'),
  },

  // 圈层邀请码管理
  inviteCodes: {
    create: (data) => request('/invite-codes/create', { method: 'POST', body: data }),
    quota: () => request('/invite-codes/quota'),
    circleList: (circleId) => request(`/invite-codes/circle/${circleId}`),
    preview: (code) => publicRequest(`/invite-codes/preview/${encodeURIComponent(code)}`),
    join: (data) => request('/invite-codes/join', { method: 'POST', body: data }),
    toggle: (codeId, active) => request(`/invite-codes/${codeId}/toggle`, { method: 'PUT', body: { active } }),
    reset: (codeId) => request(`/invite-codes/${codeId}/reset`, { method: 'PUT' }),
    delete: (codeId) => request(`/invite-codes/${codeId}`, { method: 'DELETE' }),
  },

  // 双人房间权益（处方③：邀请成功双方各得一间）
  duoRoom: {
    entitlements: () => request('/duo-room/entitlements'),
    redeem: (entitlementId, circleId) =>
      request('/duo-room/redeem', { method: 'POST', body: { entitlementId, circleId } }),
  },

  // 冷启动漏斗（作战室数据源，founder-only）
  funnel: {
    summary: () => request('/funnel/summary'),
    events: (limit) => request(`/funnel/events?limit=${limit || 50}`),
  },

  // 联邦通信
  federation: {
    status: () => request('/federation/status'),
    nodes: () => request('/federation/nodes'),
    registerNode: (serverUrl) =>
      request('/federation/nodes/register', { method: 'POST', body: { server_url: serverUrl } }),
    searchUsers: (query) =>
      request('/federation/users/search', { method: 'POST', body: { query } }),
    sendMessage: (toFederationId, message) =>
      request('/federation/message/send', { method: 'POST', body: { to_federation_id: toFederationId, message } }),
  },

  // === v5.3 增长飞轮与功能增强 ===

  // 休眠圈层
  dormant: {
    check: () => request('/dormant/check'),
    snooze: (circleId, reason) => request('/dormant/snooze', { method: 'POST', body: { circleId, reason } }),
    activate: (circleId) => request('/dormant/activate', { method: 'POST', body: { circleId } }),
    dissolve: (circleId, farewellMessage, keepData) =>
      request('/dormant/dissolve', { method: 'POST', body: { circleId, farewellMessage, keepData } }),
    exit: (data) => request('/dormant/exit', { method: 'POST', body: data }),
    exitCancel: (exitId) => request('/dormant/exit-cancel', { method: 'POST', body: { exitId } }),
    exitConfirm: (exitId) => request('/dormant/exit-confirm', { method: 'POST', body: { exitId } }),
    getExits: () => request('/dormant/exits'),
  },

  // 增长飞轮
  growth: {
    referralCard: (circleId) => request('/growth/referral-card', { method: 'POST', body: { circleId } }),
    getReferralCard: (token) => publicRequest(`/growth/referral-card/${token}`),
    transparencyReport: (month) => request('/growth/transparency-report', { method: 'POST', body: { month } }),
    transparencyReports: () => request('/growth/transparency-reports'),
    shareTransparencyReport: (token) => publicRequest(`/growth/transparency-report/share/${token}`),
    yearbook: (circleId, year) => request('/growth/yearbook', { method: 'POST', body: { circleId, year } }),
    getYearbook: (token) => publicRequest(`/growth/yearbook/share/${token}`),
    yearbooks: () => request('/growth/yearbooks'),
    templates: (params) => request(`/growth/templates?${new URLSearchParams(params).toString()}`),
    templateDetail: (id) => request(`/growth/templates/${id}`),
    createTemplate: (data) => request('/growth/templates', { method: 'POST', body: data }),
    applyTemplate: (id) => request(`/growth/templates/${id}/apply`, { method: 'POST' }),
  },

  // 场景模板市场（v8 新栈，家人/散居亲缘入口）
  templates: {
    list: (params = {}) => request(`/templates?${new URLSearchParams(params).toString()}`),
    categories: () => request('/templates/categories'),
    detail: (id) => request(`/templates/${id}`),
    use: (id, data = {}) => request(`/templates/${id}/use`, { method: 'POST', body: data }),
    star: (id) => request(`/templates/${id}/star`, { method: 'POST' }),
  },

  growthOps: {
    joinWaitlist: (data) => publicRequest('/growth-ops/waitlist', { method: 'POST', body: data }),
    waitlistStats: () => publicRequest('/growth-ops/waitlist/stats'),
    founderDashboard: () => request('/growth-ops/founder/dashboard'),
    founderWaitlist: () => request('/growth-ops/founder/waitlist'),
    founderFamilyPlanIntents: () => request('/growth-ops/founder/family-plan-intents'),
    founderChecklist: () => request('/growth-ops/founder/checklist'),
    updateChecklist: (id, data) => request(`/growth-ops/founder/checklist/${id}`, { method: 'PUT', body: data }),
  },

  // 家庭故事征集（公开投稿 + 创始人审核；投稿发生在候补成功页，用户彼时还没有账号）
  story: {
    submit: (data) => publicRequest('/story/submissions', { method: 'POST', body: data }),
    published: (limit) => publicRequest(`/story/submissions/published${limit ? `?limit=${limit}` : ''}`),
    founderList: (status = 'pending') =>
      request(`/story/founder/submissions?status=${encodeURIComponent(status)}`),
    founderReview: (id, action, note = '') =>
      request(`/story/founder/submissions/${id}/review`, { method: 'POST', body: { action, note } }),
  },

  android: {
    downloadInfo: () => publicRequest('/android/download-info'),
  },

  site: {
    legal: () => publicRequest('/site/legal'),
    wechat: () => publicRequest('/site/wechat'),
  },

  // 时间胶囊
  timeCapsule: {
    create: (data) => request('/time-capsule/create', { method: 'POST', body: data }),
    circleList: (circleId) => request(`/time-capsule/circle/${circleId}`),
    unlock: (id) => request(`/time-capsule/${id}/unlock`, { method: 'POST' }),
    mine: () => request('/time-capsule/mine'),
    dueSoon: (hours) => request(`/time-capsule/due-soon?hours=${hours || 72}`),
    refreshDue: () => request('/time-capsule/cron/refresh', { method: 'POST' }),
  },

  // 圈层表情包
  stickers: {
    upload: (formData) => request('/stickers/upload', { method: 'POST', body: formData, headers: {} }),
    circleList: (circleId, params) => {
      const query = new URLSearchParams(params || {}).toString();
      return request(`/stickers/circle/${circleId}?${query}`);
    },
    recordUse: (id) => request(`/stickers/${id}/use`, { method: 'POST' }),
    delete: (id) => request(`/stickers/${id}`, { method: 'DELETE' }),
  },

  // 关系健康度
  relationship: {
    health: () => request('/relationship/health'),
    refreshHealth: () => request('/relationship/health', { method: 'POST' }),
    graph: () => request('/relationship/graph'),
    heatmap: (days) => request(`/relationship/heatmap?days=${days || 30}`),
  },

  // 微信聊天记录导入
  wechatImport: {
    upload: (formData) => request('/wechat-import/upload', { method: 'POST', body: formData, headers: {} }),
    history: () => request('/wechat-import/history'),
    delete: (id) => request(`/wechat-import/${id}`, { method: 'DELETE' }),
    matchContacts: (data) => request('/wechat-import/match-contacts', { method: 'POST', body: data }),
    matchResults: (importId) => request(`/wechat-import/match-contacts/${importId}`),
  },

  // AI 本地助手服务（经主服务 /api/v1/ai 反代）
  // 注意：AI 出字慢（跨洋链路尤甚），一律放宽到 60s——默认 15s 会把正常回答误判成「超时出错」
  ai: {
    _aiTimeoutMs: 60000,
    status: () => request('/ai/status', { _aiService: true }),
    summarizeChat: (messages, maxMessages) =>
      request('/ai/summarize/chat', { method: 'POST', body: { messages, max_messages: maxMessages }, _aiService: true, timeoutMs: 60000 }),
    summarizeCircle: (circleName, events) =>
      request('/ai/summarize/circle', { method: 'POST', body: { circle_name: circleName, events }, _aiService: true, timeoutMs: 60000 }),
    keywords: (text, topK) =>
      request('/ai/keywords', { method: 'POST', body: { text, top_k: topK }, _aiService: true, timeoutMs: 60000 }),
    smartReply: (context, style) =>
      request('/ai/smart-reply', { method: 'POST', body: { context, style }, _aiService: true, timeoutMs: 60000 }),
    translate: (text, targetLang, sourceLang) =>
      request('/ai/translate', { method: 'POST', body: { text, target_lang: targetLang, source_lang: sourceLang }, _aiService: true, timeoutMs: 60000 }),
    translateLanguages: () => request('/ai/translate/languages', { _aiService: true }),
    detectLanguage: (text) =>
      request('/ai/detect-language', { method: 'POST', body: { text }, _aiService: true }),
    // 健康 AI
    chat: (prompt, systemPrompt, maxTokens, temperature) =>
      request('/ai/chat', {
        method: 'POST',
        body: { prompt, system_prompt: systemPrompt, max_tokens: maxTokens, temperature },
        _aiService: true,
        timeoutMs: 60000,
      }),
  },

  // 圈层相册
  album: {
    circlePhotos: (circleId, params = {}) => {
      const query = new URLSearchParams(params).toString();
      return request(`/messages/circle/${circleId}/media?${query}`);
    },
    privatePhotos: (userId, params = {}) => {
      const query = new URLSearchParams(params).toString();
      return request(`/messages/private/${userId}/media?${query}`);
    },
  },

  // 离线P2P局域网
  p2p: {
    status: () => request('/federation/lan-status'),
    discover: () => request('/federation/lan-discover', { method: 'POST' }),
    peers: () => request('/federation/lan-peers'),
    sendFile: (peerId, formData) => request(`/federation/lan-send/${peerId}`, {
      method: 'POST', body: formData, headers: {},
    }),
  },
  // 锁屏通知（Web Push）
  push: {
    status: () => request('/push/status'),
    subscribe: (payload) => request('/push/subscribe', { method: 'POST', body: payload }),
    unsubscribe: (endpoint) => request('/push/subscribe', { method: 'DELETE', body: { endpoint } }),
    test: () => request('/push/test', { method: 'POST' }),
  },
};

// WebSocket连接
export function createWebSocket(token) {
  const ws = new WebSocket(`${WS_BASE}?token=${token}`);
  return ws;
}

export { API_BASE, WS_BASE };
